import { Grid } from '@mui/material';
import { getIn, useFormikContext } from 'formik';
import { FONT_WEIGHT_OPTIONS } from 'utils/selectOptionsUtils';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import { GradientDirection } from 'components/AcGradientInput/AcGraidentInput.types';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';

import { CompletedThemeFormValues } from './types';

type ColorDefaultValue = {
  colorOne: string;
  colorTwo?: string;
  gradientDirection?: GradientDirection;
};

type CompletedThemeFontStyleFieldsProps = {
  colorName: string;
  sizeName: string;
  weightName: string;
  colorDefaultValue: ColorDefaultValue;
  sizeValue: number | string;
  weightValue?: string;
  sizeMin: number;
  sizeMax: number;
  testIdPrefix: string;
  solidColorOnly?: boolean;
};

const getFieldState = (
  touched: object,
  errors: object,
  name: string
): { error: boolean; helperText: string } => {
  const isTouched = getIn(touched, name);
  const fieldError = getIn(errors, name);

  return {
    error: Boolean(isTouched && fieldError),
    helperText: isTouched && fieldError ? String(fieldError) : ''
  };
};

export const CompletedThemeFontStyleFields: React.FC<
  CompletedThemeFontStyleFieldsProps
> = ({
  colorName,
  sizeName,
  weightName,
  colorDefaultValue,
  sizeValue,
  weightValue,
  sizeMin,
  sizeMax,
  testIdPrefix,
  solidColorOnly = false
}) => {
  const { handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<CompletedThemeFormValues>();

  const colorState = getFieldState(touched, errors, colorName);
  const sizeState = getFieldState(touched, errors, sizeName);
  const weightState = getFieldState(touched, errors, weightName);

  return (
    <>
      <Grid item xs={4}>
        <AcGradientInput
          header="Font Color"
          name={colorName}
          defaultValue={colorDefaultValue}
          setValue={setFieldValue}
          onBlur={handleBlur}
          dataTestId={`${testIdPrefix}Color`}
          dataTestPopoverId={`${testIdPrefix}ColorPopover`}
          dataTestColorOneId={`${testIdPrefix}ColorOne`}
          dataTestColorTwoId={`${testIdPrefix}ColorTwo`}
          dataTestGradientTypeId={`${testIdPrefix}ColorGradientType`}
          dataTestGradientDirectionId={`${testIdPrefix}ColorGradientDirection`}
          error={colorState.error}
          helperText={colorState.helperText}
          outputAsSingleColor={solidColorOnly}
          onlySolid={solidColorOnly}
        />
      </Grid>
      <Grid item xs={4}>
        <AcInput
          header="Font Size (px)"
          name={sizeName}
          type="number"
          dataTestId={`${testIdPrefix}Size`}
          slotProps={{
            htmlInput: {
              max: sizeMax,
              min: sizeMin
            }
          }}
          value={sizeValue}
          onChange={handleChange}
          onBlur={handleBlur}
          error={sizeState.error}
          helperText={sizeState.helperText}
        />
      </Grid>
      <Grid item xs={4}>
        <AcSelect
          header="Font Weight"
          headerSize="12"
          name={weightName}
          defaultValue="regular"
          value={weightValue}
          dataTestBoxId={`${testIdPrefix}Weight`}
          dataTestListId={`${testIdPrefix}WeightList`}
          optionDataTestIdPrefix={`${testIdPrefix}WeightOption`}
          onChange={handleChange}
          onBlur={handleBlur}
          error={weightState.error}
          items={FONT_WEIGHT_OPTIONS}
        />
      </Grid>
    </>
  );
};
